import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatBrlCurrency } from "@/lib/price";
import type { RawItem } from "@/types/models";

export interface RawItemListProps {
  rawItems: RawItem[];
  selectedId?: string | null;
  deleting?: boolean;
  onEdit: (rawItem: RawItem) => void;
  onDelete: (rawItem: RawItem) => void;
}

function RawItemActions({
  rawItem,
  deleting,
  onEdit,
  onDelete,
}: {
  rawItem: RawItem;
  deleting: boolean;
  onEdit: (rawItem: RawItem) => void;
  onDelete: (rawItem: RawItem) => void;
}) {
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => onEdit(rawItem)}>
        Editar
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={deleting}
        onClick={() => onDelete(rawItem)}
      >
        Excluir
      </Button>
    </>
  );
}

export function RawItemList({
  rawItems,
  selectedId,
  deleting = false,
  onEdit,
  onDelete,
}: RawItemListProps) {
  if (rawItems.length === 0) {
    return (
      <section className="border border-primary-200 dark:border-primary-800 panel-padding">
        <p className="text-sm font-light text-primary-600 dark:text-primary-400">
          Nenhum insumo cadastrado. Crie o primeiro insumo ao lado.
        </p>
      </section>
    );
  }

  return (
    <section className="border border-primary-200 dark:border-primary-800 overflow-hidden">
      <ul className="md:hidden divide-y divide-primary-200 dark:divide-primary-800">
        {rawItems.map((rawItem) => (
          <li
            key={rawItem.id}
            className={cn(
              "panel-padding space-y-3",
              selectedId === rawItem.id && "bg-primary-50 dark:bg-primary-900/50"
            )}
          >
            <p className="text-base font-light text-primary-900 dark:text-white">{rawItem.name}</p>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm font-light">
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500">Unidade</dt>
                <dd className="text-primary-600 dark:text-primary-400">
                  {rawItem.unitLabel || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-primary-500">
                  Custo por unidade
                </dt>
                <dd className="text-primary-900 dark:text-white">
                  {formatBrlCurrency(rawItem.costCents)}
                </dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2">
              <RawItemActions
                rawItem={rawItem}
                deleting={deleting}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          </li>
        ))}
      </ul>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[36rem] text-sm font-light">
          <thead>
            <tr className="border-b border-primary-200 dark:border-primary-800 text-left text-xs uppercase tracking-widest text-primary-500">
              <th className="px-4 py-4 lg:px-6">Nome</th>
              <th className="px-4 py-4 lg:px-6">Unidade</th>
              <th className="px-4 py-4 lg:px-6">Custo por unidade</th>
              <th className="px-4 py-4 lg:px-6 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rawItems.map((rawItem) => (
              <tr
                key={rawItem.id}
                className={cn(
                  "border-b border-primary-100 dark:border-primary-800 last:border-0",
                  selectedId === rawItem.id && "bg-primary-50 dark:bg-primary-900/50"
                )}
              >
                <td className="px-4 py-4 lg:px-6 text-primary-900 dark:text-white">
                  {rawItem.name}
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-600 dark:text-primary-400">
                  {rawItem.unitLabel || "—"}
                </td>
                <td className="px-4 py-4 lg:px-6 text-primary-900 dark:text-white">
                  {formatBrlCurrency(rawItem.costCents)}
                </td>
                <td className="px-4 py-4 lg:px-6 text-right space-x-2">
                  <RawItemActions
                    rawItem={rawItem}
                    deleting={deleting}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
